import { useCallback, useEffect, useState } from 'react'
import type { BreakdownItem } from '@domain/finance'
import type { BreakdownItemPatch, NewBreakdownItem } from '@domain/ports/finance-repository'
import type { Cents } from '@shared/money/cents'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * Os itens de detalhamento de uma transação, com as três escritas.
 *
 * ## A lista é corrigida no lugar, e não relida
 *
 * Cada escrita devolve o suficiente para saber como a lista ficou: `create`
 * devolve o id, `update` recebe o patch, `remove` recebe o id. Reler traria de
 * volta a mesma coisa depois de um round-trip — e, pior, poderia trazer o estado
 * **anterior**, porque a Function que recalcula o resumo escreve na transação
 * depois. Uma lista que pisca entre dois valores durante uma edição é como uma
 * soma de dinheiro deixa de ser confiável aos olhos de quem a lê.
 *
 * ## O resumo NÃO vem daqui
 *
 * Quem o calcula para a tela é `breakdownRemainder` sobre estes itens; quem o
 * grava é a Function. Guardar um resumo neste hook seria uma terceira cópia do
 * mesmo invariante.
 */
export type Breakdown =
  | { status: 'loading' | 'failed' }
  | {
      status: 'loaded'
      items: BreakdownItem[]
      add: (input: Omit<NewBreakdownItem, 'transactionId'>) => Promise<void>
      edit: (id: string, patch: BreakdownItemPatch) => Promise<void>
      remove: (id: string) => Promise<void>
      /** Vários de uma vez — é o que os atalhos de divisão produzem. */
      addMany: (inputs: { categoryId: string; amountCents: Cents }[]) => Promise<void>
    }

export function useBreakdown(transactionId: string): Breakdown {
  const { breakdownRepository } = useFinanceRepositories()
  const [settled, setSettled] = useState<
    { id: string; status: 'failed' } | { id: string; status: 'loaded'; items: BreakdownItem[] } | null
  >(null)

  useEffect(() => {
    let live = true

    void breakdownRepository
      .listByTransaction(transactionId)
      .then((items) => {
        if (live) setSettled({ id: transactionId, status: 'loaded', items })
      })
      .catch((error: unknown) => {
        console.error('useBreakdown: a leitura dos itens falhou', error)
        if (live) setSettled({ id: transactionId, status: 'failed' })
      })

    return () => {
      live = false
    }
  }, [breakdownRepository, transactionId])

  const aplicar = useCallback((mudar: (items: BreakdownItem[]) => BreakdownItem[]) => {
    setSettled((atual) =>
      atual === null || atual.status !== 'loaded' ? atual : { ...atual, items: mudar(atual.items) },
    )
  }, [])

  const add = useCallback(
    async (input: Omit<NewBreakdownItem, 'transactionId'>) => {
      const id = await breakdownRepository.create({ ...input, transactionId })
      aplicar((items) => [...items, novoItem(id, transactionId, input)])
    },
    [breakdownRepository, transactionId, aplicar],
  )

  const addMany = useCallback(
    async (inputs: { categoryId: string; amountCents: Cents }[]) => {
      // **Em série, não em paralelo.** Cada criação dispara a Function que
      // recalcula o resumo; disparadas juntas, elas leem a coleção em estados
      // diferentes e a última a escrever pode ser a que leu menos itens. Em
      // série, a última a escrever é a que viu todos.
      for (const input of inputs) {
        await add({ ...input, description: null })
      }
    },
    [add],
  )

  const edit = useCallback(
    async (id: string, patch: BreakdownItemPatch) => {
      await breakdownRepository.update(id, patch)
      aplicar((items) =>
        items.map((item) =>
          item.id === id
            ? {
                ...item,
                categoryId: patch.categoryId ?? item.categoryId,
                amountCents: patch.amountCents ?? item.amountCents,
                description: patch.description === undefined ? item.description : patch.description,
              }
            : item,
        ),
      )
    },
    [breakdownRepository, aplicar],
  )

  const remove = useCallback(
    async (id: string) => {
      await breakdownRepository.remove(id)
      aplicar((items) => items.filter((item) => item.id !== id))
    },
    [breakdownRepository, aplicar],
  )

  if (settled === null || settled.id !== transactionId) return { status: 'loading' }
  if (settled.status === 'failed') return { status: 'failed' }
  return { status: 'loaded', items: settled.items, add, addMany, edit, remove }
}

/**
 * O item recém-criado, como a tela precisa vê-lo até a próxima leitura.
 *
 * **Os carimbos são uma SENTINELA, não uma hora.** O relógio do aparelho não
 * entra no app (decisão 79, e o `eslint` cobra), e aqui ele não faria falta
 * nenhuma: `createdAt` só serve para ordenar, o item novo vai para o fim da
 * lista de qualquer jeito porque é assim que ele é acrescentado, e a ordenação
 * de verdade — a do servidor — chega na próxima leitura. Uma string que ordena
 * depois de qualquer ISO 8601 diz exatamente isso, e não finge ser um instante.
 *
 * `householdId` fica vazio pelo mesmo motivo: nada na tela o lê, e inventá-lo
 * seria uma cópia a mais para divergir. Ver `HOUSEHOLD_ID` no adapter, que é
 * quem sabe.
 */
const AINDA_NAO_LIDO = '~novo'

function novoItem(
  id: string,
  transactionId: string,
  input: Omit<NewBreakdownItem, 'transactionId'>,
): BreakdownItem {
  return {
    id,
    householdId: '',
    transactionId,
    categoryId: input.categoryId,
    amountCents: input.amountCents,
    description: input.description,
    origin: 'manual',
    receiptItemIds: [],
    createdAt: AINDA_NAO_LIDO,
    updatedAt: AINDA_NAO_LIDO,
  }
}
